import { useNavigate, useLocation } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Card, CardContent } from './ui/card';
import { Label } from './ui/label';
import { LayoutGrid, Building2, Users, Target, Briefcase, FileText, Image, Calendar, MapPin, Settings, LogOut, DollarSign, MessageCircle, MessageSquare, Globe, FolderOpen, ChevronDown, ChevronRight, Square, User, Mail, Menu, X, UserCircle, Wrench, Palette, Camera, ShoppingCart, Inbox, Maximize2 as Maximize2Icon, Minimize2 as Minimize2Icon, Wallet, CalendarRange } from 'lucide-react';
import NotificationBell from './NotificationBell';
import AIAssistantWidget from './AIAssistantWidget';

// ─────────────────────────────────────────────────────────────
// Raccourcis clavier — tapez 2 lettres pour ouvrir un module.
// Ex: "cl" → Clients, "pr" → Prospects, "em" → Emplacement, etc.
// Ignoré si l'utilisateur tape dans un champ (input/textarea/contentEditable).
// ─────────────────────────────────────────────────────────────
const KEYBOARD_SHORTCUTS = {
  da: '/dashboard',           // Dashboard
  en: '/entreprise',          // Entreprise
  pr: '/prospects',           // Prospects
  cl: '/clients',             // Clients
  co: '/contrats',            // Contrats
  con: '/conception',         // Conception (3 lettres)
  cn: '/conception',          // Conception (alias 2 lettres, rétro-compat)
  pl: '/planning',            // Planning
  su: '/suivi',               // Suivi
  tr: '/tarifs',              // Tarifs
  ta: '/tarifs',              // Tarifs (alias rétro-compat)
  ca: '/calendrier',          // Calendrier
  me: '/echange',             // Messagerie (route réelle)
  br: '/inbox',               // Boîte de Réception (nouveau raccourci)
  bi: '/inbox',               // Boîte de réception (alias rétro-compat)
  gm: '/boite-mail',         // Boîte mail Gmail
  em: '/emplacement',         // Emplacement
  np: '/panneaux',            // Nos Panneaux (route réelle)
  te: '/technique',           // Technique
  sa: '/saisie-commande',     // Saisie de commande
  fo: '/fournisseurs',        // Fournisseurs
  re: '/suivi?type=reservation', // Réservation (fusionnée dans Suivi)
  mp: '/mon-profil',          // Mon Profil
  ut: '/utilisateurs',        // Utilisateurs
  ad: '/admin',               // Admin
  // Sous-onglets Entreprise
  do: '/entreprise?tab=documents',   // Docs
  in: '/entreprise?tab=info',        // Infos
  on: '/entreprise?tab=objectives',  // Objectifs
  es: '/entreprise?tab=team',        // Équipe
  cp: '/entreprise?tab=finances',    // Comptabilité
  iv: '/entreprise?tab=investisseurs', // Investisseurs
  ce: '/entreprise?tab=certifications', // Certifications
  rv: '/entreprise?tab=revenus',     // Revenus
  rp: '/entreprise?tab=reports',     // Rapports
  vu: '/entreprise?tab=overview',    // Vue d'ensemble
};

// Raccourcis contextuels : actifs seulement dans le module ouvert (prioritaires sur les globaux).
const SUIVI_SHORTCUTS = {
  dv: '/suivi?type=devis', fa: '/suivi?type=facture',
  dp: '/suivi?type=devis&sub=panneaux', dc: '/suivi?type=devis&sub=conception',
  dpr: '/suivi?type=devis&sub=production', dce: '/suivi?type=devis&sub=creation_entreprise',
  fp: '/suivi?type=facture&sub=panneaux', fc: '/suivi?type=facture&sub=conception',
  fpr: '/suivi?type=facture&sub=production', fce: '/suivi?type=facture&sub=creation_entreprise',
};
const CONTEXT_SHORTCUTS = {
  '/suivi': SUIVI_SHORTCUTS,
  '/entreprise': { re: '/entreprise?tab=revenus' },
  '/conception': { cda: '/conception?ouvrir=affiches' },
};
const shortcutsFor = (pathname) => ({ ...KEYBOARD_SHORTCUTS, ...(CONTEXT_SHORTCUTS[pathname] || {}) });

// Navigation organisée par groupes
const navigationGroups = [
  {
    id: 'overview',
    label: 'Vue d\'ensemble',
    items: [
      { id: 'dashboard', label: 'Dashboard', icon: LayoutGrid, path: '/dashboard' }
    ]
  },
  {
    id: 'management',
    label: 'Gestion',
    items: [
      { id: 'entreprise', label: 'Entreprise', icon: Building2, path: '/entreprise' },
      { id: 'inbox', label: 'Boîte de réception', icon: Inbox, path: '/inbox' },
      { id: 'boite-mail', label: 'Boîte mail (Gmail)', icon: Mail, path: '/boite-mail' },
      { id: 'prospects', label: 'Prospects', icon: Target, path: '/prospects' },
      { id: 'clients', label: 'Clients', icon: Briefcase, path: '/clients' },
      { id: 'contrats', label: 'Contrats', icon: FileText, path: '/contrats' },
      { id: 'paiements', label: 'Paiements', icon: Wallet, path: '/paiements' }
    ]
  },
  {
    id: 'tracking',
    label: 'Suivi',
    items: [
      { id: 'suivi', label: 'Suivi', icon: FolderOpen, path: '/suivi' },
      { id: 'retours-clients', label: 'Retours clients', icon: MessageSquare, path: '/retours-clients' },
      { id: 'planning', label: 'Planning', icon: Calendar, path: '/planning' },
      { id: 'tarifs', label: 'Tarifs', icon: DollarSign, path: '/tarifs' },
      { id: 'calendrier', label: 'Calendrier', icon: Calendar, path: '/calendrier' },
      { id: 'echange', label: 'Messagerie', icon: MessageCircle, path: '/echange' }
    ]
  },
  {
    id: 'operations',
    label: 'Opérations',
    items: [
      { id: 'occupation', label: 'Occupation', icon: CalendarRange, path: '/occupation' },
      { id: 'emplacement', label: 'Emplacement', icon: MapPin, path: '/emplacement' },
      { id: 'panneaux', label: 'Nos Panneaux', icon: Square, path: '/panneaux' },
      { id: 'technique', label: 'Technique', icon: Wrench, path: '/technique' },
      { id: 'conception', label: 'Conception', icon: Palette, path: '/conception' },
      { id: 'saisie-commande', label: 'Saisie de commande', icon: ShoppingCart, path: '/saisie-commande' },
      { id: 'fournisseurs', label: 'Fournisseurs', icon: Building2, path: '/fournisseurs' }
    ]
  },
  {
    id: 'team',
    label: 'Équipe',
    items: [
      { id: 'mon-profil', label: 'Mon Profil', icon: UserCircle, path: '/mon-profil' },
      { id: 'utilisateurs', label: 'Utilisateurs', icon: Users, path: '/utilisateurs' }
    ]
  },
  {
    id: 'settings',
    label: 'Administration',
    items: [
      { id: 'admin', label: 'Admin', icon: Settings, path: '/admin' }
    ]
  }
];

export default function Layout({ children, user, onLogout, currentPage }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [unreadCount, setUnreadCount] = useState(0);
  const [inboxCount, setInboxCount] = useState(0);
  const [overdueCount, setOverdueCount] = useState(0);
  const [lastMessageId, setLastMessageId] = useState(null);
  const [expandedGroups, setExpandedGroups] = useState(['overview', 'management', 'content', 'tracking', 'operations', 'partners', 'team', 'settings']);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showMessageNotification, setShowMessageNotification] = useState(false);
  const [newMessageData, setNewMessageData] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  // Buffer des 2 dernières touches pour les raccourcis clavier
  const [keyBuffer, setKeyBuffer] = useState('');

  const pathRef = useRef(location.pathname);
  pathRef.current = location.pathname;
  // Raccourcis clavier — 2 lettres consécutives ouvrent le module correspondant.
  useEffect(() => {
    let resetTimer;
    const isTypingInField = () => {
      const el = document.activeElement;
      if (!el) return false;
      const tag = (el.tagName || '').toUpperCase();
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
      if (el.isContentEditable) return true;
      return false;
    };
    const onKeyDown = (e) => {
      if (e.altKey || e.ctrlKey || e.metaKey) return;
      if (isTypingInField()) return;
      const ch = (e.key || '').toLowerCase();
      if (!/^[a-z]$/.test(ch)) return;
      setKeyBuffer((prev) => {
        // Buffer glissant : 3 lettres d'abord, puis 2 ; attend la 3e lettre si un raccourci plus long existe.
        const map = shortcutsFor(pathRef.current);
        const go = (k) => { navigate(map[k]); toast.success(`Raccourci « ${k} » → ${map[k]}`); };
        const next = (prev + ch).slice(-3);
        const last2 = next.slice(-2);
        clearTimeout(resetTimer);
        if (next.length === 3 && map[next]) {
          e.preventDefault();
          go(next);
          return '';
        }
        if (last2.length === 2 && map[last2]) {
          const hasLongerPrefix = Object.keys(map).some(k => k.length === 3 && k.startsWith(last2));
          if (!hasLongerPrefix) {
            e.preventDefault();
            go(last2);
            return '';
          }
          resetTimer = setTimeout(() => { go(last2); setKeyBuffer(''); }, 400);
          return next;
        }
        resetTimer = setTimeout(() => setKeyBuffer(''), 900);
        return next;
      });
    };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      clearTimeout(resetTimer);
    };
    // eslint-disable-next-line
  }, []);

  const toggleGroup = (groupId) => {
    setExpandedGroups(prev => 
      prev.includes(groupId) 
        ? prev.filter(id => id !== groupId)
        : [...prev, groupId]
    );
  };

  useEffect(() => {
    // Load unread count
    loadUnreadCount();
    loadInboxCount();
    
    // Refresh unread count every 4 seconds
    const interval = setInterval(() => { loadUnreadCount(); loadInboxCount(); }, 4000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    // Check for new messages every 3 seconds (responsiveness)
    const messageCheckInterval = setInterval(checkForNewMessages, 3000);
    return () => clearInterval(messageCheckInterval);
  }, [lastMessageId, currentPage]);

  const loadUnreadCount = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/messages/unread-count`);
      setUnreadCount(response.data.unread_count || 0);
    } catch (error) {
      // silent
    }
  };

  const loadOverdueCount = async () => {
    try {
      const r = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/technique/stats`);
      setOverdueCount(r.data?.overdue_count || 0);
    } catch (e) { /* silent */ }
  };

  const loadInboxCount = async () => {
    try {
      const r = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/inbox/devis-requests/count-new`);
      setInboxCount(r.data?.count || 0);
    } catch (e) { /* silent */ }
  };

  useEffect(() => {
    loadOverdueCount();
    const interv = setInterval(loadOverdueCount, 60000); // toutes les 60s
    return () => clearInterval(interv);
  }, []);

  const checkForNewMessages = async () => {
    try {
      // Utilise l'endpoint moderne /api/conversations
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/conversations`);
      const conversations = response.data || [];
      
      // Trouver la conversation avec un dernier message récent + non lu
      const candidates = conversations
        .filter(c => c.unread_count > 0 && c.last_message_at)
        .sort((a, b) => new Date(b.last_message_at) - new Date(a.last_message_at));
      
      if (candidates.length > 0) {
        const latest = candidates[0];
        const latestMsgId = `${latest.id}_${latest.last_message_at}`;

        // Nouveau message + on n'est pas sur la page messagerie
        if (lastMessageId && lastMessageId !== latestMsgId && currentPage !== 'echange') {
          // Récupère le dernier message + le nom expéditeur
          let senderName = 'Quelqu\'un';
          let messageText = '...';
          try {
            const msgResp = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/conversations/${latest.id}/messages`);
            const msgs = msgResp.data || [];
            const lastMsg = msgs[msgs.length - 1];
            if (lastMsg) {
              senderName = lastMsg.sender_name || lastMsg.sender_email || 'Quelqu\'un';
              messageText = lastMsg.content || '';
            }
          } catch (e) { /* ignore */ }
          
          setNewMessageData({
            conversationId: latest.id,
            sender: senderName,
            message: messageText.length > 80 ? messageText.substring(0, 80) + '...' : messageText,
            time: new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
          });
          setShowMessageNotification(true);

          // Son de notification (optionnel)
          try {
            const audio = new Audio('data:audio/wav;base64,UklGRl9vT19XQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQAA');
            audio.volume = 0.3;
            audio.play().catch(() => {});
          } catch (e) { /* ignore */ }

          // Auto-hide après 8 secondes
          setTimeout(() => setShowMessageNotification(false), 8000);
        }
        setLastMessageId(latestMsgId);
      } else if (conversations.length > 0) {
        // Initialise lastMessageId au premier passage
        const top = [...conversations].sort((a, b) => new Date(b.last_message_at || 0) - new Date(a.last_message_at || 0))[0];
        if (top && !lastMessageId) {
          setLastMessageId(`${top.id}_${top.last_message_at}`);
        }
      }
    } catch (error) {
      // silent
    }
  };

  const handleMessageNotificationClick = () => {
    setShowMessageNotification(false);
    navigate('/echange');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-300 to-slate-400 flex">
      {/* Bouton hamburger - visible uniquement mobile */}
      <button
        onClick={() => setSidebarOpen(!sidebarOpen)}
        className="lg:hidden fixed top-3 left-3 z-[60] bg-white rounded-lg shadow-md p-2 border border-gray-200"
        data-testid="mobile-menu-toggle"
        aria-label="Menu"
      >
        {sidebarOpen ? <X className="h-5 w-5 text-gray-700" /> : <Menu className="h-5 w-5 text-gray-700" />}
      </button>

      {/* Overlay sombre quand sidebar ouverte sur mobile */}
      {sidebarOpen && (
        <div
          onClick={() => setSidebarOpen(false)}
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
        />
      )}

      {/* Sidebar Verticale — bleu nuit + full-screen toggle */}
      {!fullscreen && (
      <aside className={`w-64 bg-slate-200 border-r border-slate-300 flex flex-col shadow-2xl fixed h-full overflow-y-auto z-50 transition-transform duration-300 ${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
      }`}>
        {/* Logo */}
        <div className="p-4 border-b border-slate-300 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center shadow-md">
              <img 
                src="https://static.prod-images.emergentagent.com/jobs/0037ca77-a398-4268-9913-34bb99581bcd/images/500d31a00c846b89ad286d272905bf205bf25dfd933910a5b5233318bf9e0b50.png" 
                alt="Logo" 
                className="w-8 h-8 rounded-full object-cover"
              />
            </div>
            <div>
              <h1 className="text-lg font-bold text-slate-800">CRM SESAM</h1>
              <p className="text-xs text-slate-500">Marketing</p>
            </div>
          </div>
          <button
            onClick={() => setFullscreen(true)}
            title="Plein écran (masquer la barre latérale)"
            data-testid="btn-fullscreen"
            className="p-1.5 rounded hover:bg-slate-300 text-slate-600"
          >
            <Maximize2Icon className="h-4 w-4" />
          </button>
        </div>

        {/* Navigation Groups */}
        <nav className="flex-1 py-4">
          {(() => {
            const role = user?.role;
            // Modules autorisés spécifiquement par l'admin (override des whitelist par rôle)
            const MODULE_ALIASES = { messagerie: 'echange', 'nos-panneaux': 'panneaux' };
            const explicitAllowed = Array.isArray(user?.modules_allowed) && user.modules_allowed.length > 0 && role !== 'admin'
              ? new Set(user.modules_allowed.map((m) => MODULE_ALIASES[m] || m))
              : null;
            // Listes blanches par rôle :
            const employeeAllowed = new Set([
              'prospects', 'clients',
              'suivi', 'tarifs', 'calendrier', 'echange',
              'emplacement', 'occupation', 'mail',
              'saisie-commande',
              'fournisseurs',
              'mon-profil',
            ]);
            const technicienAllowed = new Set([
              'entreprise', 'technique', 'echange', 'mon-profil',
            ]);
            return navigationGroups
              .filter(group => {
                if (role === 'investisseur' || role === 'associe') {
                  return group.id === 'overview';
                }
                return true;
              })
              .map(group => {
                // Override admin : liste explicite (mon-profil toujours dispo)
                if (explicitAllowed) {
                  const items = group.items.filter(it => explicitAllowed.has(it.id) || it.id === 'mon-profil');
                  return { ...group, items };
                }
                if (role === 'employee' || role === 'employer') {
                  const items = group.items.filter(it => employeeAllowed.has(it.id));
                  return { ...group, items };
                }
                if (role === 'technicien') {
                  const items = group.items.filter(it => technicienAllowed.has(it.id));
                  return { ...group, items };
                }
                return group;
              })
              .filter(group => group.items.length > 0);
          })()
            .map((group) => (
            <div key={group.id} className="mb-2">
              {/* Group Header */}
              <button
                onClick={() => toggleGroup(group.id)}
                className="w-full flex items-center justify-between px-4 py-2 text-[11px] font-bold text-slate-600 uppercase tracking-wider hover:bg-slate-300 transition-colors"
              >
                <span>{group.label}</span>
                {expandedGroups.includes(group.id) ? (
                  <ChevronDown className="h-4 w-4" />
                ) : (
                  <ChevronRight className="h-4 w-4" />
                )}
              </button>

              {/* Group Items */}
              {expandedGroups.includes(group.id) && (
                <div className="mt-1 px-2 space-y-1">
                  {group.items.map((item) => {
                    const Icon = item.icon;
                    const isActive = currentPage === item.id;

                    return (
                      <button
                        key={item.id}
                        onClick={() => { navigate(item.path); setSidebarOpen(false); }}
                        data-testid={`nav-${item.id}`}
                        className={`group w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm border transition-all duration-200 transform ${
                          isActive
                            ? 'bg-blue-600 text-white border-blue-500 font-semibold shadow-lg scale-[1.02]'
                            : 'text-slate-700 bg-white border-slate-300 hover:bg-slate-100 hover:border-slate-400 hover:shadow-md hover:-translate-y-0.5'
                        }`}
                      >
                        <Icon className={`h-4 w-4 flex-shrink-0 transition-transform duration-200 ${isActive ? 'text-white scale-110' : 'text-slate-600 group-hover:scale-110'}`} />
                        <span className="flex-1 text-left">{item.label}</span>
                        {item.badge && (
                          <span className="bg-blue-100 text-blue-700 border border-blue-300 text-[10px] font-bold rounded-md px-1.5 py-0.5">
                            {item.badge}
                          </span>
                        )}
                        {item.id === 'echange' && unreadCount > 0 && (
                          <span className="bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                            {unreadCount > 9 ? '9+' : unreadCount}
                          </span>
                        )}
                        {item.id === 'inbox' && inboxCount > 0 && (
                          <span className="relative flex items-center" data-testid="inbox-count-badge">
                            <span className="absolute inline-flex h-5 w-5 rounded-full bg-blue-400 opacity-75 animate-ping"></span>
                            <span className="relative bg-blue-600 text-white text-xs font-bold rounded-full min-w-5 h-5 px-1.5 flex items-center justify-center">
                              {inboxCount > 9 ? '9+' : inboxCount}
                            </span>
                          </span>
                        )}
                        {item.id === 'technique' && overdueCount > 0 && (
                          <span className="relative flex items-center" data-testid="technique-overdue-badge">
                            <span className="absolute inline-flex h-5 w-5 rounded-full bg-red-400 opacity-75 animate-ping"></span>
                            <span className="relative bg-red-500 text-white text-xs font-bold rounded-full min-w-5 h-5 px-1.5 flex items-center justify-center">
                              {overdueCount > 9 ? '9+' : overdueCount}
                            </span>
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
        </nav>

        {/* User Info at Bottom */}
        <div className="p-4 border-t border-slate-300">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-green-400 to-blue-500 flex items-center justify-center overflow-hidden shadow-sm">
              {user?.avatar_url ? (
                <img src={user.avatar_url} alt="avatar" className="w-full h-full object-cover" />
              ) : (
                <span className="text-white text-xs font-bold">
                  {user?.name?.charAt(0) || user?.email?.charAt(0) || 'U'}
                </span>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-slate-800 truncate">
                {user?.name || user?.email}
              </p>
              <p className="text-xs text-slate-500">{user?.role || 'Utilisateur'}</p>
            </div>
          </div>
        </div>
      </aside>
      )}

      {/* Bouton flottant pour ré-afficher la sidebar en mode plein écran */}
      {fullscreen && (
        <button
          onClick={() => setFullscreen(false)}
          data-testid="btn-exit-fullscreen"
          title="Afficher la barre latérale"
          className="fixed top-4 left-4 z-50 p-2 bg-slate-900 text-white rounded-lg shadow-lg hover:bg-slate-800"
        >
          <Minimize2Icon className="h-4 w-4" />
        </button>
      )}

      {/* Main Content Area */}
      <div className={`flex-1 w-full min-w-0 ${fullscreen ? '' : 'lg:ml-64'}`}>
        {/* Header - Simplified */}
        <header className="bg-white border-b border-gray-200 sticky top-0 z-30 shadow-sm">
          <div className="px-3 sm:px-6 py-3 sm:py-4 pl-14 lg:pl-6">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="min-w-0">
                <h2 className="text-lg sm:text-2xl font-bold text-gray-900 truncate">SESAM</h2>
                <p className="text-[10px] sm:text-sm text-gray-600 uppercase tracking-wide hidden sm:block">(Service aux Sociétés Africaines en Marketing)</p>
              </div>
              <div className="flex items-center gap-2 sm:gap-3 flex-wrap justify-end">
                {/* Badge En ligne */}
                <div className="flex items-center gap-1.5 px-2 sm:px-3 py-1 sm:py-1.5 bg-green-50 border border-green-200 rounded-full">
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                  <span className="text-xs sm:text-sm font-medium text-green-700">En ligne</span>
                </div>

                {/* Cloche notifications */}
                <NotificationBell />

                {/* Informations utilisateur cliquable - ouvre Mon Profil */}
                {/* Bouton profil + upload de photo */}
                <div className="flex items-center gap-2 sm:gap-3 px-2 sm:px-4 py-1.5 sm:py-2 bg-gray-50 rounded-lg border border-gray-200 hover:bg-blue-50 hover:border-blue-300 transition-colors">
                  <label
                    htmlFor="header-avatar-upload"
                    className="relative group cursor-pointer flex-shrink-0"
                    title="Cliquer pour changer votre photo"
                    data-testid="header-avatar-upload-label"
                  >
                    <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center shadow-md overflow-hidden">
                      {user?.avatar_url ? (
                        <img src={user.avatar_url} alt="avatar" className="w-full h-full object-cover" />
                      ) : (
                        <span className="text-white text-xs sm:text-sm font-bold">
                          {user?.name?.charAt(0) || user?.email?.charAt(0) || 'U'}
                        </span>
                      )}
                    </div>
                    <div className="absolute inset-0 rounded-full bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <Camera className="h-4 w-4 text-white" />
                    </div>
                    <input
                      id="header-avatar-upload"
                      type="file"
                      accept="image/*"
                      className="hidden"
                      data-testid="header-avatar-input"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        if (file.size > 2 * 1024 * 1024) {
                          try { toast.error('Photo trop volumineuse (max 2 Mo)'); } catch {}
                          return;
                        }
                        const reader = new FileReader();
                        reader.onload = async (ev) => {
                          const base64 = ev.target?.result;
                          try {
                            await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/profiles/${encodeURIComponent(user.email)}`, {
                              avatar_url: base64,
                            });
                            try { toast.success('Photo mise à jour'); } catch {}
                            // Recharge la page pour prendre en compte le nouvel avatar partout
                            window.location.reload();
                          } catch (err) {
                            try { toast.error('Erreur lors de la mise à jour de la photo'); } catch {}
                          }
                        };
                        reader.readAsDataURL(file);
                      }}
                    />
                  </label>
                  <button
                    onClick={() => navigate('/mon-profil')}
                    data-testid="header-profile-btn"
                    className="hidden sm:flex flex-col text-left cursor-pointer"
                    title="Voir mon profil"
                  >
                    <p className="text-sm font-bold text-gray-900 truncate max-w-[150px]">
                      {user?.name || user?.email}
                    </p>
                    <p className="text-xs text-gray-500">
                      {user?.role === 'admin' ? 'Administrateur' : 
                       user?.role === 'investisseur' ? 'Investisseur' : 
                       user?.role === 'associe' ? 'Investisseur' :
                       user?.role === 'technicien' ? 'Technicien' :
                       'Employé'}
                    </p>
                  </button>
                </div>

                {/* Bouton Paramètres - icon only on mobile */}
                <Button 
                  variant="outline" 
                  size="sm"
                  className="gap-2 border-gray-300 hover:bg-gray-50"
                  onClick={() => setShowSettingsModal(true)}
                >
                  <Settings className="h-4 w-4" />
                  <span className="hidden sm:inline">Paramètres</span>
                </Button>

                {/* Bouton Déconnexion - icon only on mobile */}
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={onLogout}
                  className="gap-2 text-red-600 border-red-200 hover:bg-red-50"
                  data-testid="logout-button"
                >
                  <LogOut className="h-4 w-4" />
                  <span className="hidden sm:inline">Déconnexion</span>
                </Button>
              </div>
            </div>
          </div>

        </header>

        {/* Page Content */}
        <main className="p-3 sm:p-6">
          {children}
        </main>
      </div>

      {/* Widget IA flottant global */}
      <AIAssistantWidget user={user} />

      {/* Modal Paramètres */}
      <Dialog open={showSettingsModal} onOpenChange={setShowSettingsModal}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-2xl">
              <Settings className="h-6 w-6 text-blue-600" />
              Paramètres du CRM SESAM
            </DialogTitle>
            <DialogDescription>
              Gérez les paramètres de votre compte et consultez les informations du système
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 mt-6">
            {/* Section Profil */}
            <Card>
              <CardContent className="p-4">
                <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
                  <User className="h-5 w-5 text-blue-600" />
                  Informations du Profil
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm text-gray-600">Nom</Label>
                    <p className="font-semibold text-gray-900">{user?.name || 'Non renseigné'}</p>
                  </div>
                  <div>
                    <Label className="text-sm text-gray-600">Email</Label>
                    <p className="font-semibold text-gray-900">{user?.email || 'Non renseigné'}</p>
                  </div>
                  <div>
                    <Label className="text-sm text-gray-600">Rôle</Label>
                    <Badge className={
                      user?.role === 'admin' ? 'bg-indigo-100 text-indigo-700' :
                      user?.role === 'investisseur' ? 'bg-green-100 text-green-700' :
                      user?.role === 'associe' ? 'bg-amber-100 text-amber-700' :
                      user?.role === 'technicien' ? 'bg-purple-100 text-purple-700' :
                      'bg-blue-100 text-blue-700'
                    }>
                      {user?.role === 'admin' ? 'Administrateur' : 
                       user?.role === 'investisseur' ? 'Investisseur' : 
                       user?.role === 'associe' ? 'Investisseur' :
                       user?.role === 'technicien' ? 'Technicien' :
                       'Employé'}
                    </Badge>
                  </div>
                  <div>
                    <Label className="text-sm text-gray-600">Statut</Label>
                    <Badge className="bg-green-100 text-green-700">En ligne</Badge>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Section À propos */}
            <Card>
              <CardContent className="p-4">
                <h3 className="font-bold text-lg mb-4">À propos du CRM SESAM</h3>
                <div className="space-y-3 text-sm text-gray-700">
                  <p><strong>Nom complet :</strong> Service aux Sociétés Africaines en Marketing (SESAM)</p>
                  <p><strong>Version :</strong> 2.0.0</p>
                  <p><strong>Dernière mise à jour :</strong> Janvier 2026</p>
                  <p><strong>Type :</strong> Solution complète pour la publicité et la communication visuelle</p>
                </div>
              </CardContent>
            </Card>

            {/* Section CGU */}
            <Card>
              <CardContent className="p-4">
                <h3 className="font-bold text-lg mb-4">Conditions Générales d'Utilisation (CGU)</h3>
                <div className="space-y-3 text-sm text-gray-700 max-h-60 overflow-y-auto bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-base">1. Objet</h4>
                  <p>
                    Les présentes Conditions Générales d'Utilisation (CGU) régissent l'accès et l'utilisation 
                    du CRM SESAM, une plateforme de gestion de la relation client destinée aux activités de 
                    publicité et communication visuelle.
                  </p>

                  <h4 className="font-semibold text-base mt-4">2. Accès au Service</h4>
                  <p>
                    L'accès au CRM est réservé aux utilisateurs disposant d'identifiants valides. 
                    Chaque utilisateur est responsable de la confidentialité de ses identifiants.
                  </p>

                  <h4 className="font-semibold text-base mt-4">3. Utilisation des Données</h4>
                  <p>
                    Les données collectées sont utilisées exclusivement pour la gestion des prospects, 
                    clients, contrats et opérations publicitaires. SESAM s'engage à protéger vos données 
                    conformément aux réglementations en vigueur.
                  </p>

                  <h4 className="font-semibold text-base mt-4">4. Propriété Intellectuelle</h4>
                  <p>
                    Tous les éléments du CRM SESAM (code, design, logo, contenus) sont la propriété 
                    exclusive de SESAM et sont protégés par les lois sur la propriété intellectuelle.
                  </p>

                  <h4 className="font-semibold text-base mt-4">5. Responsabilités</h4>
                  <p>
                    SESAM met tout en œuvre pour assurer la disponibilité et la sécurité du service. 
                    Toutefois, SESAM ne saurait être tenu responsable des interruptions de service 
                    dues à des causes techniques ou de force majeure.
                  </p>

                  <h4 className="font-semibold text-base mt-4">6. Modifications</h4>
                  <p>
                    SESAM se réserve le droit de modifier les présentes CGU à tout moment. 
                    Les utilisateurs seront informés de toute modification importante.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Section Contact & Support */}
            <Card>
              <CardContent className="p-4">
                <h3 className="font-bold text-lg mb-4">Contact & Support</h3>
                <div className="space-y-2 text-sm text-gray-700">
                  <p className="flex items-center gap-2">
                    <Mail className="h-4 w-4 text-blue-600" />
                    <strong>Email:</strong> support@sesam.com
                  </p>
                  <p className="flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-blue-600" />
                    <strong>Adresse:</strong> Bangui, République Centrafricaine
                  </p>
                  <p className="flex items-center gap-2">
                    <Building2 className="h-4 w-4 text-blue-600" />
                    <strong>Entreprise:</strong> SESAM - Service aux Sociétés Africaines en Marketing
                  </p>
                </div>
              </CardContent>
            </Card>

            <div className="flex justify-end pt-4 border-t">
              <Button onClick={() => setShowSettingsModal(false)} className="bg-blue-600 hover:bg-blue-700">
                Fermer
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Notification de nouveau message - Clignotante en haut */}
      {showMessageNotification && newMessageData && (
        <>
          <style>{`
            @keyframes msgFlash {
              0%, 100% { box-shadow: 0 0 30px rgba(59, 130, 246, 0.9); transform: scale(1); }
              50% { box-shadow: 0 0 60px rgba(239, 68, 68, 1); transform: scale(1.04); }
            }
            @keyframes msgSlide {
              0% { transform: translateX(110%); opacity: 0; }
              100% { transform: translateX(0); opacity: 1; }
            }
          `}</style>
          <div 
            className="fixed top-6 right-6 z-[9999] bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 text-white rounded-2xl shadow-2xl border-4 border-yellow-300 p-5 cursor-pointer max-w-md w-[420px]"
            onClick={handleMessageNotificationClick}
            style={{
              animation: 'msgSlide 0.4s ease-out, msgFlash 1s ease-in-out infinite'
            }}
            data-testid="message-notification-popup"
          >
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 rounded-full bg-white/25 flex items-center justify-center flex-shrink-0 animate-bounce">
                <MessageCircle className="h-8 w-8 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-bold text-lg flex items-center gap-2 mb-1">
                  💬 Nouveau message !
                </p>
                <p className="text-sm font-semibold text-yellow-200 truncate">{newMessageData.sender}</p>
                <p className="text-sm text-white/95 mt-1 line-clamp-2 break-words">{newMessageData.message}</p>
                <div className="flex items-center justify-between mt-2">
                  <p className="text-xs text-blue-100">{newMessageData.time}</p>
                  <span className="text-xs bg-white/20 px-2 py-0.5 rounded-full">Cliquez pour ouvrir →</span>
                </div>
              </div>
              <button
                className="text-white/80 hover:text-white text-xl ml-1 flex-shrink-0"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowMessageNotification(false);
                }}
              >
                ✕
              </button>
            </div>
            {/* Pastille rouge clignotante */}
            <div className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 rounded-full animate-ping"></div>
            <div className="absolute -top-2 -right-2 w-5 h-5 bg-red-600 rounded-full"></div>
          </div>
        </>
      )}
    </div>
  );
}
